<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ $t("dmp.areaSchedules.title") }}</h5>
              <div class="d-flex gap-1">
                <b-button variant="primary" size="sm" @click="addNewAreaSchedule"
                  :disabled="isLoadingAreaSchedules || isSyncingAreaSchedules" class="text-capitalize">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.areaSchedules.addNew") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoadingAreaSchedules" class="text-center py-4">
              <b-spinner variant="primary" :label="$t('dmp.areaSchedules.loading')"></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.areaSchedules.loading") }}...</p>
            </div>

            <b-row v-if="!isLoadingAreaSchedules">
              <b-col cols="2" class="table-column">
                <!-- Area Schedules Table -->
                <b-table :items="localAreaSchedules" :fields="areaScheduleTableFields" responsive striped hover
                  show-empty :empty-text="$t('dmp.areaSchedules.noSchedulesConfigured')" selectable select-mode="single"
                  @row-selected="onRowSelected" sort-by="number" :sort-desc="false" class="mb-0"
                  :tbody-tr-class="rowClass">
                  <template #cell(number)="data">
                    {{ parseInt(data.item.areaNumber) }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="10" class="details-column">
                <!-- Selected Area Schedule Details Card -->
                <b-card v-if="selectedAreaSchedule" no-body>
                  <b-card-header class="mb-1 pt-0">
                    <h6 class="mb-0">{{ $t("dmp.areaSchedules.details") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="areaScheduleFormObserver">
                      <b-form @submit.prevent="saveAreaSchedule">
                        <b-row>
                          <!-- Area Number -->
                          <b-col cols="12" md="6">
                            <validation-provider #default="validationContext" :name="$t('dmp.areaSchedules.areaNumber')"
                              rules="required">
                              <b-form-group :label="$t('dmp.areaSchedules.areaNumber')" label-for="area-number">
                                <b-form-select id="area-number" v-model="selectedAreaSchedule.areaNumber"
                                  :options="areaSelectOptions" :state="getValidationState(validationContext)"
                                  :disabled="isEditingAreaSchedule" />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>
                          <!-- Schedules Section -->
                          <b-col cols="12">
                            <b-form-group :label="$t('dmp.areaSchedules.schedules')">
                              <p class="text-muted small mb-2">
                                {{ $t("dmp.areaSchedules.schedulesHint") }}
                              </p>
                              <b-row>
                                <b-col cols="12" md="6" v-for="index in 8" :key="`schedule-${index}`" class="mb-2">
                                  <b-form-group :label="`${index}.`" :label-for="`schedule-${index}`" label-cols="1">
                                    <b-form-select :id="`schedule-${index}`"
                                      v-model="selectedAreaSchedule[`schedule${index}`]"
                                      :options="scheduleSelectOptions" />
                                  </b-form-group>
                                </b-col>
                              </b-row>
                            </b-form-group>
                          </b-col>

                          <!-- Action Buttons -->
                          <b-col cols="12" class="d-flex justify-content-end mt-2">
                            <b-button v-if="isEditingAreaSchedule" variant="outline-danger" size="sm" class="mr-1"
                              @click="deleteAreaSchedule" :disabled="isSavingAreaSchedule || isDeletingAreaSchedule">
                              <b-spinner v-if="isDeletingAreaSchedule" small class="mr-25" />
                              <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                              <span>{{ $t("actions.delete") }}</span>
                            </b-button>
                            <b-button variant="outline-secondary" size="sm" class="mr-1" @click="cancelEdit"
                              :disabled="isSavingAreaSchedule || isDeletingAreaSchedule">
                              <feather-icon icon="XIcon" size="14" class="mr-25" />
                              <span>{{ $t("actions.cancel") }}</span>
                            </b-button>
                            <b-button variant="primary" size="sm" type="submit"
                              :disabled="isSavingAreaSchedule || isDeletingAreaSchedule">
                              <b-spinner v-if="isSavingAreaSchedule" small class="mr-25" />
                              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                              <span>{{
                                isSavingAreaSchedule
                                  ? $t("actions.saving")
                                  : $t("actions.save")
                              }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>

                <!-- Empty State -->
                <b-card v-else no-body class="d-flex align-items-center justify-content-center"
                  style="min-height: 300px">
                  <div class="text-center p-3">
                    <feather-icon icon="CalendarIcon" size="48" class="text-muted mb-2" />
                    <p class="text-muted mb-0">
                      {{ $t("dmp.areaSchedules.selectArea") }}
                    </p>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>

  </div>
</template>

<script>
import { ref, computed, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import { useTemplateMode } from "./useTemplateMode";
import { formatToTwoDigits } from "./usePanelConfig";

export default {
  name: "DmpPanelsConfigAreaSchedules",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    areaSchedulesData: {
      type: Array,
      default: () => [],
    },
    timeSchedulesData: {
      type: Array,
      default: () => [],
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();
    const toast = useToast();

    // Form validation
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);
    const areaScheduleFormObserver = ref(null);


    // State
    const selectedAreaSchedule = ref(null);
    const isEditingAreaSchedule = ref(false);
    const isSavingAreaSchedule = ref(false);
    const isDeletingAreaSchedule = ref(false);
    const isLoadingAreaSchedules = ref(false);
    const isSyncingAreaSchedules = ref(false);
    const timeSchedules = computed(() => props.timeSchedulesData || []);

    const scheduleSelectOptions = computed(() => {
      const options = [{ value: "", text: proxy.$t("dmp.profiles.noSchedule") }];
      timeSchedules.value.forEach((schedule) => {
        options.push({
          value: String(schedule.scheduleNumber).padStart(2, "0"),
          text: `${parseInt(schedule.scheduleNumber)} - ${String(schedule.scheduleName)}`,
        });
      });
      return options;
    });


    const areaSelectOptions = computed(() => {
      const areas = areasOptions.value;
      if (!Array.isArray(areas)) return [];
      return areas.map((area) => ({
        value: String(area.id).padStart(2, "0"),
        text: `${parseInt(area.id)} - ${area.name}`,
      }));
    });


    const areasOptions = computed(() => {
      return store.getters["dmp_panels/getAreasList"] || [];
    });

    // Table fields
    const areaScheduleTableFields = computed(() => [
      {
        key: "number",
        label: proxy.$t("dmp.areaSchedules.number"),
        sortable: true,
      },
    ]);

    // Initialize area schedules data
    const initializeAreaSchedules = () => {
      // Only show schedules that exist in props
      if (!props.areaSchedulesData) return [];

      return [...props.areaSchedulesData].sort(
        (a, b) => parseInt(a.areaNumber) - parseInt(b.areaNumber)
      );
    };

    const localAreaSchedules = computed(() => initializeAreaSchedules());

    // Row selection
    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];

        const normalizeSchedule = (val) => {
          if (!val || val === "--" || val === "00" || val === "0") return "";
          return val;
        };

        selectedAreaSchedule.value = {
          ...selected,
          schedule1: normalizeSchedule(selected.schedule1),
          schedule2: normalizeSchedule(selected.schedule2),
          schedule3: normalizeSchedule(selected.schedule3),
          schedule4: normalizeSchedule(selected.schedule4),
          schedule5: normalizeSchedule(selected.schedule5),
          schedule6: normalizeSchedule(selected.schedule6),
          schedule7: normalizeSchedule(selected.schedule7),
          schedule8: normalizeSchedule(selected.schedule8),
        };
        isEditingAreaSchedule.value = true;
      } else {
        selectedAreaSchedule.value = null;
        isEditingAreaSchedule.value = false;
      }
    };

    // Add new area schedule
    const addNewAreaSchedule = () => {
      // Find next available area number
      let nextAreaNumber = 1;
      for (let i = 1; i <= 32; i++) {
        const areaNum = String(i).padStart(2, "0");
        const exists = props.areaSchedulesData.find((s) => s.areaNumber === areaNum);
        if (!exists) {
          nextAreaNumber = i;
          break;
        }
      }

      selectedAreaSchedule.value = {
        areaNumber: String(nextAreaNumber).padStart(2, "0"),
        schedule1: "",
        schedule2: "",
        schedule3: "",
        schedule4: "",
        schedule5: "",
        schedule6: "",
        schedule7: "",
        schedule8: "",
      };
      isEditingAreaSchedule.value = false;
    };

    // Format area number
    const formatAreaNumber = () => {
      if (selectedAreaSchedule.value?.areaNumber) {
        selectedAreaSchedule.value.areaNumber = formatToTwoDigits(
          selectedAreaSchedule.value.areaNumber
        );
      }
    };

    // Format schedule
    const formatSchedule = (index) => {
      const field = `schedule${index}`;
      if (
        selectedAreaSchedule.value?.[field] &&
        selectedAreaSchedule.value[field].trim() !== ""
      ) {
        selectedAreaSchedule.value[field] = formatToTwoDigits(
          selectedAreaSchedule.value[field]
        );
      }
    };

    // Cancel edit
    const cancelEdit = () => {
      selectedAreaSchedule.value = null;
      isEditingAreaSchedule.value = false;
    };

    // Save area schedule
    const saveAreaSchedule = async () => {
      const isValid = await areaScheduleFormObserver.value.validate();
      if (!isValid) return;

      isSavingAreaSchedule.value = true;

      try {
        // Prepare payload - remove empty schedules
        const payload = {
          areaNumber: selectedAreaSchedule.value.areaNumber,
          schedule1: selectedAreaSchedule.value.schedule1 || "",
          schedule2: selectedAreaSchedule.value.schedule2 || "",
          schedule3: selectedAreaSchedule.value.schedule3 || "",
          schedule4: selectedAreaSchedule.value.schedule4 || "",
          schedule5: selectedAreaSchedule.value.schedule5 || "",
          schedule6: selectedAreaSchedule.value.schedule6 || "",
          schedule7: selectedAreaSchedule.value.schedule7 || "",
          schedule8: selectedAreaSchedule.value.schedule8 || "",
        };

        if (isTemplateMode.value) {
          const list = [...(props.areaSchedulesData || [])];
          const idx = list.findIndex(
            (s) =>
              parseInt(s.areaNumber, 10) === parseInt(payload.areaNumber, 10)
          );
          const item = { ...(idx !== -1 ? list[idx] : {}), ...payload };
          if (idx !== -1) {
            item.id = list[idx].id != null ? list[idx].id : nextSequentialId(list);
            list.splice(idx, 1, item);
          } else {
            item.id = nextSequentialId(list);
            list.push(item);
          }

          emitTemplateUpdate("area_schedules", list);
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.areaSchedules.messages.scheduleSaveSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedAreaSchedule.value = null;
          isEditingAreaSchedule.value = false;
          isSavingAreaSchedule.value = false;
          return;
        }

        const response = await store.dispatch("dmp_panels/setAreaSchedule", {
          uuid: props.uuid,
          areaScheduleData: payload,
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.areaSchedules.messages.scheduleSaveSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        // Emit event to notify operation completed
        emit("operation-completed");

        // Clear selection
        selectedAreaSchedule.value = null;
        isEditingAreaSchedule.value = false;
      } catch (error) {
        console.error("Error saving area schedule:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.areaSchedules.messages.scheduleSaveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSavingAreaSchedule.value = false;
      }
    };

    // Delete area schedule
    const deleteAreaSchedule = async () => {
      const result = await Swal.fire({
        title: proxy.$t("dmp.areaSchedules.confirmDelete"),
        text: proxy.$t("dmp.areaSchedules.confirmDeleteMessage"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      if (isTemplateMode.value) {
        const list = (props.areaSchedulesData || []).filter(
          (s) =>
            parseInt(s.areaNumber, 10) !==
            parseInt(selectedAreaSchedule.value.areaNumber, 10)
        );
        emitTemplateUpdate("area_schedules", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.areaSchedules.messages.scheduleDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedAreaSchedule.value = null;
        isEditingAreaSchedule.value = false;
        return;
      }

      isDeletingAreaSchedule.value = true;

      try {
        await store.dispatch("dmp_panels/deleteAreaSchedule", {
          uuid: props.uuid,
          areaNumber: selectedAreaSchedule.value.areaNumber,
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.areaSchedules.messages.scheduleDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        // Emit event to notify operation completed
        emit("operation-completed");

        // Clear selection
        selectedAreaSchedule.value = null;
        isEditingAreaSchedule.value = false;
      } catch (error) {
        console.error("Error deleting area schedule:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.areaSchedules.messages.scheduleDeleteError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isDeletingAreaSchedule.value = false;
      }
    };

    // Row class for highlighting
    const rowClass = (item, type) => {
      if (!item || type !== "row") return "";
      if (selectedAreaSchedule.value?.areaNumber === item.areaNumber) {
        return "table-active";
      }
      return "";
    };

    watch(
      () => props.areaSchedulesData,
      (newData) => {
        if (newData) {
          // Data is already computed in localAreaSchedules
        }
      },
      { deep: true }
    );

    watch(
      () => props.isActive,
      (newVal) => {
        if (newVal) {
          fetchTimeSchedules();
        }
      }
    );

    return {
      // Form
      refFormObserver,
      areaScheduleFormObserver,
      getValidationState,

      // State
      selectedAreaSchedule,
      isEditingAreaSchedule,
      isSavingAreaSchedule,
      isDeletingAreaSchedule,
      isLoadingAreaSchedules,
      isSyncingAreaSchedules,
      scheduleSelectOptions,

      // Table
      areaScheduleTableFields,
      localAreaSchedules,

      // Methods
      onRowSelected,
      addNewAreaSchedule,
      formatAreaNumber,
      formatSchedule,
      cancelEdit,
      saveAreaSchedule,
      deleteAreaSchedule,
      rowClass,
      timeSchedules,
      areaSelectOptions,
    };
  },
};
</script>

<style lang="scss" scoped>
.table-column {
  max-height: 600px;
  overflow-y: auto;
}

.details-column {
  max-height: 600px;
  overflow-y: auto;
}

.card-header-custom {
  background-color: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
  padding: 1rem;
}

.dark-layout {
  .card-header-custom {
    background-color: #283046;
    border-bottom-color: #404656;
  }
}

::v-deep {
  .table-active {
    background-color: rgba(115, 103, 240, 0.12) !important;
  }

  .dark-layout .table-active {
    background-color: rgba(115, 103, 240, 0.2) !important;
  }
}
</style>
